import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CircleDollarSign,
  Plus,
  Search,
  UserRoundCheck,
  X,
} from "lucide-react";
import type { AccountLink, AcquisitionAttribution, Contact, Opportunity, Organization } from "./types";
import {
  filterOpportunities,
  opportunitySummary,
} from "./lib/opportunityModel";
import { captureOpportunityLearning, closeOpportunityWithLearning, getCurrentUserId, manageOpportunity, recordMeaningfulSalesInteraction } from "./lib/api";
import { RecordNotes } from "./RecordNotes";
import { DemoLaunchAction } from './DemoLaunchAction'
import {useCrmResourceDenied} from './lib/crmResourceAccess'
import {purgeCrmResourceDrafts} from './lib/crmSync'
import { resolveDemoAudience } from './lib/salesDemo'
const money = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
const localDate = (iso?: string) =>
  iso
    ? new Date(
        new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
type Form = {
  name: string;
  organizationId: string;
  primaryContactId: string;
  stage: Opportunity["stage"];
  annualValue: string;
  nextAction: string;
  nextActionDueAt: string;
  primaryPain:string;buyingTrigger:string;mostValuableOutcome:string;mostValuableCapability:string;primaryObjection:string;competitorOrAlternative:string;lostReason:string;wonReason:string;requiredIntegration:string;whatTheyDidNotUnderstand:string;nuance:string;
};
const blank = (): Form => ({
  name: "",
  organizationId: "",
  primaryContactId: "",
  stage: "discovery",
  annualValue: "",
  nextAction: "",
  nextActionDueAt: "",
  primaryPain:"",buyingTrigger:"",mostValuableOutcome:"",mostValuableCapability:"",primaryObjection:"",competitorOrAlternative:"",lostReason:"",wonReason:"",requiredIntegration:"",whatTheyDidNotUnderstand:"",nuance:"",
});
export function OpportunityWorkspace({
  opportunities,
  setOpportunities,
  organizations,
  contacts,
  accountLinks,
  acquisitionAttributions,
}: {
  opportunities: Opportunity[];
  setOpportunities: (value: Opportunity[]) => void;
  organizations: Organization[];
  contacts: Contact[];
  accountLinks: AccountLink[];
  acquisitionAttributions: AcquisitionAttribution[];
}) {
  const navigate = useNavigate();
  const [params]=useSearchParams(),linkedRecordOpened=useRef('');
  const denied=useCrmResourceDenied('opportunities');
  const requestEpoch=useRef(0),deniedRef=useRef(denied);deniedRef.current=denied;
  const [query, setQuery] = useState(""),
    [stage, setStage] = useState("open"),
    [selectedRecord, setSelected] = useState<Opportunity | null>(null),
    [open, setOpen] = useState(false),
    [editingRecord, setEditing] = useState<Opportunity | null>(null),
    [form, setForm] = useState<Form>(blank),
    [state, setState] = useState<"idle" | "saving" | "error">("idle"),
    [error, setError] = useState("");
  useEffect(()=>{if(denied){requestEpoch.current++;setSelected(null);setEditing(null);setForm(blank());setOpen(false);setState('idle');setError('');purgeCrmResourceDrafts('opportunities')}},[denied]);
  useEffect(()=>()=>{requestEpoch.current++},[]);
  useEffect(()=>{const id=params.get('opportunity');if(!id||denied||linkedRecordOpened.current===id)return;const record=opportunities.find(item=>item.id===id);if(record){linkedRecordOpened.current=id;setSelected(record)}},[params,opportunities,denied]);
  const editing=editingRecord?opportunities.find(item=>item.id===editingRecord.id)||editingRecord:null;
  const selected = selectedRecord ? opportunities.find(item=>item.id===selectedRecord.id) || selectedRecord : null;
  const selectedAvailable=!selectedRecord||opportunities.some(item=>item.id===selectedRecord.id);
  const visible = useMemo(
      () => filterOpportunities(opportunities, query, stage),
      [opportunities, query, stage],
    ),
    summary = opportunitySummary(opportunities);
  const selectedOrganization = organizations.find(item => item.id === selected?.organizationId)
  const selectedAttribution = acquisitionAttributions.find(item => item.opportunityId === selected?.id || item.organizationId === selected?.organizationId)
  const selectedDemoAudience = resolveDemoAudience({
    ownerAppAccountId: selectedOrganization?.ownerAppAccountId,
    agencyAccountId: selectedOrganization?.agencyAccountId,
    productLanes: accountLinks.filter(item => item.organizationId === selected?.organizationId && item.linkStatus === 'active').map(item => item.productLane),
    leadType: selectedAttribution?.leadType,
  })
  function begin(item?: Opportunity) {
    if(deniedRef.current)return;
    setEditing(item || null);
    setForm(
      item
        ? {
            name: item.name,
            organizationId: item.organizationId,
            primaryContactId: item.primaryContactId || "",
            stage: item.stage,
            annualValue:
              item.annualValueCents === undefined
                ? ""
                : String(item.annualValueCents / 100),
            nextAction: item.nextAction || "",
            nextActionDueAt: localDate(item.nextActionDueAt),
            primaryPain:"",buyingTrigger:"",mostValuableOutcome:"",mostValuableCapability:"",primaryObjection:"",competitorOrAlternative:"",lostReason:"",wonReason:"",requiredIntegration:"",whatTheyDidNotUnderstand:"",nuance:"",
          }
        : blank(),
    );
    setError("");
    setState("idle");
    setOpen(true);
  }
  async function save(
    e: React.FormEvent,
    action: "CREATE" | "UPDATE" | "REOPEN" = editing?.stage === "won" ||
    editing?.stage === "lost"
      ? "REOPEN"
      : editing
        ? "UPDATE"
        : "CREATE",
  ) {
    e.preventDefault();
    if(deniedRef.current)return;
    const epoch=requestEpoch.current;
    if(editingRecord&&!opportunities.some(item=>item.id===editingRecord.id)){setError("Selected opportunity is absent from the bounded result; deletion or access is unverified.");return}
    if (!form.name.trim() || !form.organizationId) {
      setError("Opportunity name and company are required.");
      return;
    }
    if(form.stage==="won"&&(!form.primaryPain.trim()||!form.wonReason.trim())){setError("Won opportunities require the primary pain and won reason.");return}
    if(form.stage==="lost"&&(!form.primaryObjection.trim()||!form.lostReason)){setError("Closed-lost opportunities require the lost reason and primary blocker.");return}
    setState("saving");
    try {
      const closing=form.stage==="won"||form.stage==="lost",priorOpenStage=editing&&!['won','lost'].includes(editing.stage)?editing.stage:"decision";
      const ownerUserId=editing?.ownerUserId || (await getCurrentUserId());
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      let saved = await manageOpportunity({
        action,
        opportunityId: editing?.id || crypto.randomUUID(),
        organizationId: form.organizationId,
        primaryContactId: form.primaryContactId || null,
        ownerUserId,
        name: form.name,
        stage: closing?priorOpenStage:form.stage,
        annualValueCents: Math.round(Number(form.annualValue || 0) * 100),
        nextAction: form.nextAction,
        nextActionDueAt: form.nextActionDueAt
          ? new Date(form.nextActionDueAt).toISOString()
          : null,
      });
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      const learning={opportunityId:saved.id,primaryPain:form.primaryPain,buyingTrigger:form.buyingTrigger,mostValuableOutcome:form.mostValuableOutcome,mostValuableCapability:form.mostValuableCapability,primaryObjection:form.primaryObjection,competitorOrAlternative:form.competitorOrAlternative,lostReason:form.lostReason||null,wonReason:form.wonReason,requiredIntegration:form.requiredIntegration,whatTheyDidNotUnderstand:form.whatTheyDidNotUnderstand,nuance:form.nuance};
      if(closing){await closeOpportunityWithLearning({...learning,stage:form.stage as 'won'|'lost'});saved={...saved,stage:form.stage}}
      else if(Object.values(learning).some((value,key)=>key>0&&Boolean(value)))await captureOpportunityLearning(learning);
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      setOpportunities([
        saved,
        ...opportunities.filter((x) => x.id !== saved.id),
      ]);
      setSelected(saved);
      setOpen(false);
    } catch (value) {
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      setState("error");
      setError(
        value instanceof Error
          ? value.message.replaceAll("_", " ")
          : "Opportunity could not be saved.",
      );
    }
  }
  async function recordMeaningfulInteraction() {
    if (deniedRef.current || !selected || !selectedAvailable) return;
    const epoch=requestEpoch.current;
    setState("saving");
    setError("");
    try {
      await recordMeaningfulSalesInteraction({
        interactionId: crypto.randomUUID(),
        organizationId: selected.organizationId,
        contactId: selected.primaryContactId || null,
        opportunityId: selected.id,
        interactionType: "MANUAL_SUBSTANTIVE",
        occurredAt: new Date().toISOString(),
      });
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      setState("idle");
    } catch (value) {
      if(deniedRef.current||epoch!==requestEpoch.current)return;
      setState("error");
      setError(value instanceof Error ? value.message.replaceAll("_", " ") : "Meaningful interaction could not be recorded.");
    }
  }
  if(denied)return <div className="runtime-banner degraded" role="alert">Opportunity access is unavailable. Selected details and unsaved opportunity drafts have been cleared. Revalidating automatically.</div>;
  return (
    <div className="page opportunity-workspace-page">
      {!selectedAvailable&&<p role="status">Selected opportunity is absent from the current bounded result. Its deletion or access status is unverified; refresh before acting.</p>}
      <div className="page-heading">
        <div>
          <span className="eyebrow">ORGANIZATION-FIRST PIPELINE</span>
          <h1>Opportunities</h1>
          <p>
            Persistent sales stages, ownership, value, and accountable next
            actions.
          </p>
        </div>
        <button className="primary-btn" onClick={() => begin()}>
          <Plus size={15} />
          Add opportunity
        </button>
      </div>
      <section className="task-summary-grid">
        <div>
          <span>Open opportunities</span>
          <strong>{summary.open}</strong>
        </div>
        <div>
          <span>Open pipeline</span>
          <strong>{money(summary.pipelineCents)}</strong>
        </div>
        <div>
          <span>Unassigned</span>
          <strong>{summary.unassigned}</strong>
        </div>
        <div>
          <span>Missing dated action</span>
          <strong>{summary.missingNextAction}</strong>
        </div>
      </section>
      <section className="panel opportunity-controls">
        <label>
          <Search size={14} />
          <input
            aria-label="Search opportunities"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Company, contact, opportunity, next action"
          />
        </label>
        <select
          aria-label="Filter opportunity stage"
          value={stage}
          onChange={(e) => setStage(e.target.value)}
        >
          {[
            "open",
            "all",
            "discovery",
            "qualified",
            "proposal",
            "decision",
            "won",
            "lost",
          ].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </section>
      <section className="panel opportunity-record-table">
        {visible.map((record) => (
          <article key={record.id} onClick={() => setSelected(record)}>
            <div className="opportunity-record-value">
              <CircleDollarSign size={15} />
              <strong>
                {record.annualValueCents === undefined
                  ? "Unknown value"
                  : money(record.annualValueCents)}
              </strong>
            </div>
            <div>
              <span>{record.stage}</span>
              <h2>{record.organizationName}</h2>
              <strong>{record.name}</strong>
              <small>{record.primaryContactName || "No primary contact"}</small>
            </div>
            <div className="opportunity-accountability">
              <span>
                <UserRoundCheck size={13} />
                {record.ownerUserId ? "Assigned" : "Unassigned"}
              </span>
              <span>
                <CalendarClock size={13} />
                {record.nextActionDueAt || "No due date"}
              </span>
              <strong>{record.nextAction || "No next action recorded"}</strong>
            </div>
          </article>
        ))}
        {!visible.length && (
          <div className="task-empty">
            <AlertTriangle size={24} />
            <h2>No matching opportunities</h2>
          </div>
        )}
      </section>
      {selected && (
        <aside className="schedule-detail">
          <header>
            <div>
              <span>{selected.stage}</span>
              <h2>{selected.name}</h2>
            </div>
            <button
              aria-label="Close opportunity"
              onClick={() => setSelected(null)}
            >
              ×
            </button>
          </header>
          <div className="schedule-detail-body">
            <p>{selected.organizationName}</p>
            <p>
              <strong>Value:</strong> {money(selected.annualValueCents || 0)}
            </p>
            <p>
              <strong>Next:</strong> {selected.nextAction || "Not recorded"}
            </p>
            <RecordNotes recordType="opportunity" recordId={selected.id} />
          </div>
          <footer>
            {selectedAvailable&&<><DemoLaunchAction audience={selectedDemoAudience} organizationId={selected.organizationId} contactId={selected.primaryContactId} opportunityId={selected.id} />
            <button onClick={()=>navigate(`/calendar?meeting=new&contact=${selected.primaryContactId||""}&organization=${selected.organizationId}&opportunity=${selected.id}&title=${encodeURIComponent(`Call · ${selected.name}`)}`)}>
              <CalendarClock size={14}/> Schedule a Call
            </button>
            <button
              onClick={() =>
                navigate(
                  `/tasks?contact=${selected.primaryContactId || ""}&organization=${selected.organizationId}&title=${encodeURIComponent(`Follow up · ${selected.name}`)}`,
                )
              }
            >
              Create follow-up
            </button>
            <button onClick={recordMeaningfulInteraction} disabled={state === "saving"}>
              {state === "saving" ? "Recording…" : "Record meaningful interaction"}
            </button>
            <button className="primary-btn" onClick={() => begin(selected)}>
              {selected.stage === "won" || selected.stage === "lost"
                ? "Reopen / Edit"
                : "Edit opportunity"}
            </button></>}
          </footer>
        </aside>
      )}
      {open && (
        <div className="record-modal-backdrop">
          <form className="record-modal" onSubmit={save}>
            <header>
              <h2>{editing ? "Edit opportunity" : "Create opportunity"}</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </header>
            <label>
              Name
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </label>
            <label>
              Company
              <select
                value={form.organizationId}
                onChange={(e) =>
                  setForm({ ...form, organizationId: e.target.value })
                }
                required
              >
                <option value="">Select company</option>
                {organizations.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Primary contact
              <select
                value={form.primaryContactId}
                onChange={(e) =>
                  setForm({ ...form, primaryContactId: e.target.value })
                }
              >
                <option value="">No primary contact</option>
                {contacts.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Stage
              <select
                value={form.stage}
                onChange={(e) =>
                  setForm({
                    ...form,
                    stage: e.target.value as Opportunity["stage"],
                  })
                }
              >
                {[
                  "discovery",
                  "qualified",
                  "proposal",
                  "decision",
                  "won",
                  "lost",
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Expected annual value
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.annualValue}
                onChange={(e) =>
                  setForm({ ...form, annualValue: e.target.value })
                }
              />
            </label>
            <label>
              Next action
              <textarea
                value={form.nextAction}
                onChange={(e) =>
                  setForm({ ...form, nextAction: e.target.value })
                }
              />
            </label>
            <label>
              Next action due
              <input
                type="datetime-local"
                value={form.nextActionDueAt}
                onChange={(e) =>
                  setForm({ ...form, nextActionDueAt: e.target.value })
                }
              />
            </label>
            <details className="opportunity-learning" open={form.stage==="won"||form.stage==="lost"}><summary>Customer learning {form.stage==="won"||form.stage==="lost"?"(required to close)":"(optional quick capture)"}</summary><div className="learning-form-grid"><label>Primary pain<input value={form.primaryPain} onChange={e=>setForm({...form,primaryPain:e.target.value})} required={form.stage==="won"}/></label><label>Buying trigger<input value={form.buyingTrigger} onChange={e=>setForm({...form,buyingTrigger:e.target.value})}/></label><label>Most valuable outcome<input value={form.mostValuableOutcome} onChange={e=>setForm({...form,mostValuableOutcome:e.target.value})}/></label><label>Most valuable capability<input value={form.mostValuableCapability} onChange={e=>setForm({...form,mostValuableCapability:e.target.value})}/></label><label>Primary objection / blocker<input value={form.primaryObjection} onChange={e=>setForm({...form,primaryObjection:e.target.value})} required={form.stage==="lost"}/></label><label>Competitor or alternative<input value={form.competitorOrAlternative} onChange={e=>setForm({...form,competitorOrAlternative:e.target.value})}/></label>{form.stage==="lost"&&<label>Lost reason<select value={form.lostReason} required onChange={e=>setForm({...form,lostReason:e.target.value})}><option value="">Select reason</option>{["PRICE","NO_LONGER_NEEDED","DID_NOT_SEE_VALUE","SETUP_TOO_DIFFICULT","TECHNICAL_PROBLEM","MISSING_INTEGRATION","SWITCHING_PROVIDER","AGENCY_MANAGED","BUSINESS_CLOSED","PAYMENT_FAILURE","TIMING","NO_DECISION","COMPETITOR","OTHER"].map(reason=><option key={reason}>{reason}</option>)}</select></label>}{form.stage==="won"&&<label>Won reason<input value={form.wonReason} required onChange={e=>setForm({...form,wonReason:e.target.value})}/></label>}<label>Required integration<input value={form.requiredIntegration} onChange={e=>setForm({...form,requiredIntegration:e.target.value})}/></label><label>What was unclear?<input value={form.whatTheyDidNotUnderstand} onChange={e=>setForm({...form,whatTheyDidNotUnderstand:e.target.value})}/></label><label className="wide">Nuance<textarea value={form.nuance} onChange={e=>setForm({...form,nuance:e.target.value})}/></label></div></details>
            {state === "error" && (
              <div className="composer-error" role="alert">
                {error}
              </div>
            )}
            <footer>
              <button type="button" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="primary-btn" disabled={state === "saving"}>
                {state === "saving" ? "Saving…" : "Save opportunity"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
